import _extends from 'babel-runtime/helpers/extends';
import { getPronunciationModel, getStepCardModel, getStepModel } from '../init';

// The percentage of steps that need to be correct for the activity to be "correct" vs "incorrect"
var MIN_ACTIVITY_PASS_STEP_PERCENT = 0.75;
var MIN_SEQ_PASS_SCORE = 0.8;
var MIN_COURSE_COMPLETE_PERCENT = 0.8;
var SCORABLE_SUBSTEP_PASSING_SCORE = 0.8;

// **** STEPS

var isPassingScoreForStep = function isPassingScoreForStep(score) {
  return score === 1;
};

// this is asking "was the step ever attempted in a non-skipped way"
var stepWithoutSubstepsIsComplete = function stepWithoutSubstepsIsComplete(activityProgress, stepId) {
  for (var i = 0; i < activityProgress.attempts.length; i++) {
    var activityAttempt = activityProgress.attempts[i];
    for (var j = 0; j < activityAttempt.steps.length; j++) {
      var activityAttemptStep = activityAttempt.steps[j];
      if (activityAttemptStep.activityStepId === stepId) {
        // any unskipped attempts mean it was completed for the purpose of this method
        if (activityAttemptStep.attempts.some(function (stepAttempt) {
          return !stepAttempt.skipped;
        })) {
          return true;
        }
      }
    }
  }
  return false;
};

// *** ACTIVITIES

var activityWasAttempted = function activityWasAttempted(activityProgress) {
  return !!activityProgress;
};

// complete does not mean correct
var activityIsComplete = function activityIsComplete(activityProgress) {
  return !!activityProgress && activityProgress.percentComplete === 1;
};

// this is the current definition of "skipped" for an activity
var activityIsSkipped = function activityIsSkipped(activityProgress) {
  return activityWasAttempted(activityProgress) && activityProgress.percentComplete < 1;
};

var activityIsIncorrect = function activityIsIncorrect(activityProgress) {
  return activityIsComplete(activityProgress) && activityProgress.bestGrade < MIN_ACTIVITY_PASS_STEP_PERCENT;
};

// Correct: all steps were attempted and 75% or more of the steps were correct (if scored) or completed (if unscored)
//  NOTE that activity.bestGrade is sum of best scores of steps / number of steps
var activityIsCorrect = function activityIsCorrect(activityProgress) {
  return activityIsComplete(activityProgress) && activityProgress.bestGrade >= MIN_ACTIVITY_PASS_STEP_PERCENT;
};

// *** we should only test this if we already know that the percent complete is 1,
//  else the activity score is 0 regardless
var getTreeActivityScore = function getTreeActivityScore(activityProgress) {
  // look through all activity attempts and find the best score for each step
  //  if any step has a best score of 0 then that activity attempt fails with 0
  for (var i = 0; i < activityProgress.attempts.length; i++) {
    var activityAttempt = activityProgress.attempts[i];
    if (!Array.isArray(activityAttempt.steps) || activityAttempt.steps.length === 0) {
      continue;
    }
    var isSkipped = activityAttempt.steps.some(function (activityAttemptStep) {
      return activityAttemptStep.skipped;
    });
    if (isSkipped) {
      continue;
    }
    // if there is any unanswered step attempt then this activityAttempt is a failure
    var allStepsAnswered = true;
    for (var j = 0; j < activityAttempt.steps.length; j++) {
      var stepProgress = activityAttempt.steps[j];
      var stepIsAnswered = false;
      for (var k = 0; k < stepProgress.attempts.length; k++) {
        var activityStepAttempt = stepProgress.attempts[k];
        if (activityStepAttempt.score === 1) {
          stepIsAnswered = true;
          break;
        }
      }
      if (!stepIsAnswered) {
        allStepsAnswered = false;
        break;
      }
    }
    if (!allStepsAnswered) {
      // this attempt gets a bestScore of 0
      continue;
    }
    // this attempt was successful all the way through and so the activity has a best score of 1
    return 1;
  }
  return 0;
};

var getTreeActivityPercentComplete = function getTreeActivityPercentComplete(activityProgress, activity) {
  var _loop = function _loop(i) {
    var activityAttempt = activityProgress.attempts[i];
    if (!Array.isArray(activityAttempt.steps) || activityAttempt.steps.length === 0) {
      return 'continue';
    }
    var isSkipped = activityAttempt.steps.some(function (activityAttemptStep) {
      return activityAttemptStep.skipped;
    });
    if (isSkipped) {
      return 'continue';
    }

    // this is a test for "are there more steps that were not attempted yet" in this activity attempt
    //  it finds the most recent stepAttempt in any step of this activityAttempt and sees if there
    //  is any nextStep available for it.  If not, then the step attempt is complete.
    var mostRecentStepId = void 0;
    var mostRecentStepAttemptDate = 0;
    for (var y = 0; y < activityAttempt.steps.length; y++) {
      var stepProgress = activityAttempt.steps[y];
      for (var z = 0; z < stepProgress.attempts.length; z++) {
        var activityStepAttempt = stepProgress.attempts[z];
        var stepAttemptDate = new Date(activityStepAttempt.endTimestamp);
        if (stepAttemptDate > mostRecentStepAttemptDate) {
          mostRecentStepAttemptDate = stepAttemptDate;
          mostRecentStepId = stepProgress.activityStepId;
        }
      }
    }
    var mostRecentStep = activity.steps.find(function (step) {
      return step.activityStepId === mostRecentStepId;
    });
    var hasUnattemptedStep = mostRecentStep.postProcess && (mostRecentStep.postProcess.nextStep || mostRecentStep.postProcess.successResponses && mostRecentStep.postProcess.successResponses[0] && mostRecentStep.postProcess.successResponses[0].nextStep);

    if (hasUnattemptedStep) {
      return 'continue';
    }
    // this activity attempt has passed all the checks, it is complete
    return {
      v: 1
    };
  };

  // look for a completed activity attempt and return 1 if there is one
  for (var i = 0; i < activityProgress.attempts.length; i++) {
    var _ret = _loop(i);

    switch (_ret) {
      case 'continue':
        continue;

      default:
        if (typeof _ret === "object") return _ret.v;
    }
  }
  // none of the attempts was complete, the activity is not complete
  return 0;
};

var parseSubstepAnswer = function parseSubstepAnswer(substepAnswer) {
  var substepAnswerInfo = void 0;
  if (substepAnswer && substepAnswer.answer) {
    var _substepAnswer$answer = substepAnswer.answer.split(':'),
        prefix = _substepAnswer$answer[0],
        contentId = _substepAnswer$answer[1],
        scoreString = _substepAnswer$answer[2],
        skipped = _substepAnswer$answer[3];

    if (prefix === 'SS') {
      substepAnswerInfo = {
        contentId: contentId,
        score: parseFloat(scoreString),
        skipped: skipped === 'true'
      };
      if (isNaN(substepAnswerInfo.score)) {
        substepAnswerInfo.score = 0;
      }
    } else if (substepAnswer.correct) {
      // See PDT-249 and AnswerFormat.kt SingleAnswerIdFormat
      substepAnswerInfo = {
        contentId: substepAnswer.answer,
        score: substepAnswer.correct ? 1 : 0,
        skipped: !substepAnswer.correct
      };
    }
  }
  return substepAnswerInfo;
};

// for a step, collect the highest scores of all step attempts that involve substeps
//  and return an array of { contentId, score }, using the max score of all substeps
//  for a given contentId (substepId)
//
//  NOTE that we need the whole activityProgress so we can review all the activity attempts
//   and their separate steps.attempts - the learner could do one substep one time and a
//   different substep the next attempt, and we pick the best of each
var getSubstepsBestScores = function getSubstepsBestScores(activityProgress, stepId) {
  var subStepInfos = [];
  if (!!activityProgress && Array.isArray(activityProgress.attempts)) {
    for (var i = 0; i < activityProgress.attempts.length; i++) {
      var _activityAttempt = activityProgress.attempts[i];
      for (var j = 0; j < _activityAttempt.steps.length; j++) {
        var stepProgress = _activityAttempt.steps[j];
        if (stepProgress.activityStepId === stepId) {
          for (var k = 0; k < stepProgress.attempts.length; k++) {
            var activityStepAttempt = stepProgress.attempts[k];

            var _loop2 = function _loop2(x) {
              var answerInfo = parseSubstepAnswer(activityStepAttempt.answers[x]);
              if (answerInfo && !answerInfo.skipped) {
                var substepInfo = subStepInfos.find(function (info) {
                  return info.contentId === answerInfo.contentId;
                });
                if (!substepInfo) {
                  substepInfo = {
                    contentId: answerInfo.contentId,
                    score: answerInfo.score
                  };
                  subStepInfos.push(substepInfo);
                } else {
                  substepInfo.score = Math.max(answerInfo.score, substepInfo.score);
                }
              }
            };

            for (var x = 0; x < activityStepAttempt.answers.length; x++) {
              _loop2(x);
            }
          }
        }
      }
    }
  }
  return subStepInfos;
};

var getBestScoreForStepWithSubsteps = function getBestScoreForStepWithSubsteps(activityProgress, step) {
  // sum all the best substep scores and divide by the total number of substeps in the step
  // one should only call this on steps which we know have substeps
  var substepCount = getSubstepCount(step);
  if (substepCount === 0) {
    // content error?
    return 0;
  }
  var scoreSum = 0;
  var subStepInfos = getSubstepsBestScores(activityProgress, step.activityStepId);
  subStepInfos.forEach(function (ssInfo) {
    if (ssInfo.score >= SCORABLE_SUBSTEP_PASSING_SCORE) {
      scoreSum++;
    }
  });
  return scoreSum / substepCount;
};

var getBestScoreForStepWithoutSubsteps = function getBestScoreForStepWithoutSubsteps(activityProgress, stepId) {
  var bestScore = 0;
  for (var i = 0; i < activityProgress.attempts.length; i++) {
    var _activityAttempt2 = activityProgress.attempts[i];
    for (var j = 0; j < _activityAttempt2.steps.length; j++) {
      var stepProgress = _activityAttempt2.steps[j];
      if (stepProgress.activityStepId === stepId) {
        for (var k = 0; k < stepProgress.attempts.length; k++) {
          var activityStepAttempt = stepProgress.attempts[k];
          if (activityStepAttempt.score !== null) {
            bestScore = Math.max(bestScore, activityStepAttempt.score);
            if (bestScore === 1) {
              // short circuit here because it can't get bigger than 1
              return 1;
            }
          }
        }
      }
    }
  }
  return bestScore;
};

// *** SEQUENCES
var sequenceWasStarted = function sequenceWasStarted(sequenceProgress) {
  return !!sequenceProgress;
};

var countOfCompletedActivities = function countOfCompletedActivities(sequenceProgress) {
  var completedActivities = 0;
  if (sequenceProgress && Array.isArray(sequenceProgress.activities)) {
    sequenceProgress.activities.forEach(function (actProgress) {
      if (activityIsComplete(actProgress)) {
        completedActivities++;
      }
    });
  }
  return completedActivities;
};

var countOfCorrectActivities = function countOfCorrectActivities(sequenceProgress) {
  var correctActivities = 0;
  if (sequenceProgress && Array.isArray(sequenceProgress.activities)) {
    sequenceProgress.activities.forEach(function (actProgress) {
      if (activityIsCorrect(actProgress)) {
        correctActivities++;
      }
    });
  }
  return correctActivities;
};

var countOfIncorrectActivities = function countOfIncorrectActivities(sequenceProgress) {
  var incorrectActivities = 0;
  if (sequenceProgress && Array.isArray(sequenceProgress.activities)) {
    sequenceProgress.activities.forEach(function (actProgress) {
      if (activityIsIncorrect(actProgress)) {
        incorrectActivities++;
      }
    });
  }
  return incorrectActivities;
};

var countOfSkippedActivities = function countOfSkippedActivities(sequenceProgress) {
  var skippedActivities = 0;
  if (sequenceProgress && Array.isArray(sequenceProgress.activities)) {
    sequenceProgress.activities.forEach(function (actProgress) {
      if (activityIsSkipped(actProgress)) {
        skippedActivities++;
      }
    });
  }
  return skippedActivities;
};

// FIXME: see below

var sequenceScore = function sequenceScore(sequenceProgress) {
  if (!sequenceProgress || sequenceProgress.countOfActivities === 0 || !Array.isArray(sequenceProgress.activities)) {
    return 0;
  }
  var sumOfActivityScores = sequenceProgress.activities.reduce(function (sum, a) {
    return sum + a.bestGrade;
  }, 0);
  var score = sumOfActivityScores / sequenceProgress.countOfActivities;

  return score;
};

var sequenceIsComplete = function sequenceIsComplete(sequenceProgress) {
  return !!sequenceProgress && sequenceProgress.percentComplete >= MIN_SEQ_PASS_SCORE;
};

var sequenceIsPassing = function sequenceIsPassing(sequenceProgress) {
  return !!sequenceProgress && sequenceProgress.percentComplete >= MIN_SEQ_PASS_SCORE && sequenceProgress.bestGrade >= MIN_SEQ_PASS_SCORE;
};

// Needs improvement: all activities in the lesson have been attempted
//   and the score is less than 80%
var sequenceNeedsImprovement = function sequenceNeedsImprovement(sequenceProgress) {
  return !sequenceIsComplete(sequenceProgress) || !sequenceIsPassing(sequenceProgress);
};

// *** COURSE
var courseWasStarted = function courseWasStarted(courseProgress) {
  return !!courseProgress && Array.isArray(courseProgress.sequences) && courseProgress.sequences.length > 0;
};

var countOfCompletedSequences = function countOfCompletedSequences(courseProgress) {
  if (!courseProgress || !Array.isArray(courseProgress.sequences)) {
    return 0;
  }
  var completedSequenceCount = 0;
  courseProgress.sequences.forEach(function (sequenceProgress) {
    if (sequenceIsComplete(sequenceProgress)) {
      completedSequenceCount++;
    }
  });
  return completedSequenceCount;
};

var countOfPassingSequences = function countOfPassingSequences(courseProgress) {
  if (!courseProgress || !Array.isArray(courseProgress.sequences)) {
    return 0;
  }
  var passedSequenceCount = 0;
  courseProgress.sequences.forEach(function (sequenceProgress) {
    if (sequenceIsPassing(sequenceProgress)) {
      passedSequenceCount++;
    }
  });
  return passedSequenceCount;
};

var percentOfCompletedSequences = function percentOfCompletedSequences(courseProgress) {
  var completedSequenceCount = countOfCompletedSequences(courseProgress);
  if (completedSequenceCount === 0) {
    return 0;
  }
  return completedSequenceCount / courseProgress.countOfSequencesInCourse;
};

var percentOfPassingSequences = function percentOfPassingSequences(courseProgress) {
  var passedSequenceCount = countOfPassingSequences(courseProgress);
  if (passedSequenceCount === 0) {
    return 0;
  }
  return passedSequenceCount / courseProgress.countOfSequencesInCourse;
};

var courseIsComplete = function courseIsComplete(courseProgress) {
  var percentComplete = percentOfCompletedSequences(courseProgress);
  return percentComplete >= MIN_COURSE_COMPLETE_PERCENT;
};

// **************** misc utils
//
var stepIsScorable = function stepIsScorable(step) {
  if (!step) {
    return false;
  }
  return getPronunciationModel().isPronunciation(step) || Array.isArray(step.correct) && step.correct.length > 0;
};

var activityIsScorable = function activityIsScorable(activity) {
  return !!activity && Array.isArray(activity.steps) && activity.steps.some(function (step) {
    return stepIsScorable(step);
  });
};

var stepHasSubsteps = function stepHasSubsteps(step) {
  return getStepModel().isCarouselStep(step) || getPronunciationModel().isPronunciation(step) && getPronunciationModel().isMultiplePronunciation(step);
};

var getSubstepCount = function getSubstepCount(step) {
  var substepCount = 0;
  if (getPronunciationModel().isPronunciation(step)) {
    substepCount = getPronunciationModel().getCountOfPronunciationItems(step);
  } else if (getStepCardModel().isExplanationStep(step)) {
    substepCount = getStepCardModel().getExplanationsCount(step);
  } else {
    // must be a vocabulary step
    substepCount = getStepCardModel().getVocabularyItemsCount(step);
  }
  return substepCount;
};

// find the sequence progress, if there is one, in the courseProgress
var getSequenceProgress = function getSequenceProgress(courseProgress, sequenceId) {
  if (!courseProgress || !Array.isArray(courseProgress.sequences)) {
    return null;
  }
  return courseProgress.sequences.find(function (s) {
    return s.sequenceId === sequenceId || s.id === sequenceId;
  });
};

var getActivityProgress = function getActivityProgress(sequenceProgress, activityId) {
  if (!sequenceProgress || !Array.isArray(sequenceProgress.activities)) {
    return null;
  }
  return sequenceProgress.activities.find(function (activity) {
    return activity.activityId === activityId;
  });
};

var extendedActivityIsUngradable = function extendedActivityIsUngradable(activityProgress) {
  return !!activityProgress && activityProgress.bestGrade === -1;
};

// given a course-progress and a sequence, figure out which activities
//  in that sequence have any progress
// we are reusing activitiesWithProgress array so it does not trigger new renders
//  every time it is calculated
var activitiesWithProgress = [];
var getActivitiesWithProgress = function getActivitiesWithProgress(courseProgress, sequence) {
  if (!sequence) {
    return null;
  }
  var sequenceProgress = getSequenceProgress(courseProgress, sequence.sequenceId);
  // Iterate each activity in the sequence:
  var newActivities = sequence.activities.map(function (activity) {
    // Find the progress for the current activity.
    var activityProgress = void 0;
    if (sequenceProgress && sequenceProgress.activities) {
      activityProgress = sequenceProgress.activities.find(function (ap) {
        return ap.activityId === activity.activityId;
      });
    }
    return _extends({}, activity, {
      progress: activityProgress
    });
  });

  // only return a new array if its contents change, to prevent excessive rendering
  if (activitiesWithProgress.length !== newActivities.length) {
    activitiesWithProgress = newActivities;
  } else {
    for (var x = 0; x < newActivities.length; x++) {
      if (newActivities[x].activityId !== activitiesWithProgress[x].activityId || newActivities[x].progress !== activitiesWithProgress[x].progress) {
        activitiesWithProgress = newActivities;
        break;
      }
    }
  }
  return activitiesWithProgress;
};

// Given a fragment of a Progress at the Course level and the number of sequences
// in the whole course, return a DTO with:
// 1) percent of lessons passed,
// 2) number of lessons passed,
// 3) "is this course considered started", and
// 4) "is this course considered completed"
var getCourseProgressInfo = function getCourseProgressInfo(courseProgress) {
  var progressInfo = {
    percentLessonsPassed: 0,
    percentLessonsCompleted: 0,
    lessonsPassed: countOfPassingSequences(courseProgress),
    lessonsCompleted: countOfCompletedSequences(courseProgress),
    isCourseCompleted: false,
    isCourseStarted: courseWasStarted(courseProgress)
  };
  if (progressInfo.lessonsPassed > 0) {
    progressInfo.percentLessonsPassed = progressInfo.lessonsPassed / courseProgress.countOfSequencesInCourse;
  }
  if (progressInfo.lessonsCompleted > 0) {
    progressInfo.percentLessonsCompleted = progressInfo.lessonsCompleted / courseProgress.countOfSequencesInCourse;
    progressInfo.isCourseCompleted = courseIsComplete(courseProgress);
  }
  return progressInfo;
};

export var progressModel = {
  // steps
  isPassingScoreForStep: isPassingScoreForStep,
  stepWithoutSubstepsIsComplete: stepWithoutSubstepsIsComplete,
  // activities
  activityWasAttempted: activityWasAttempted,
  activityIsComplete: activityIsComplete,
  activityIsSkipped: activityIsSkipped,
  activityIsIncorrect: activityIsIncorrect,
  activityIsCorrect: activityIsCorrect,
  getTreeActivityPercentComplete: getTreeActivityPercentComplete,
  getTreeActivityScore: getTreeActivityScore,
  parseSubstepAnswer: parseSubstepAnswer,
  getSubstepsBestScores: getSubstepsBestScores,
  getBestScoreForStepWithSubsteps: getBestScoreForStepWithSubsteps,
  getBestScoreForStepWithoutSubsteps: getBestScoreForStepWithoutSubsteps,
  // sequences
  countOfCompletedActivities: countOfCompletedActivities,
  countOfCorrectActivities: countOfCorrectActivities,
  countOfIncorrectActivities: countOfIncorrectActivities,
  countOfSkippedActivities: countOfSkippedActivities,
  sequenceScore: sequenceScore,
  sequenceWasStarted: sequenceWasStarted,
  sequenceIsComplete: sequenceIsComplete,
  sequenceIsPassing: sequenceIsPassing,
  sequenceNeedsImprovement: sequenceNeedsImprovement,
  // course
  countOfPassingSequences: countOfPassingSequences,
  countOfCompletedSequences: countOfCompletedSequences,
  percentOfPassingSequences: percentOfPassingSequences,
  percentOfCompletedSequences: percentOfCompletedSequences,

  courseWasStarted: courseWasStarted,
  courseIsComplete: courseIsComplete,

  // utils
  extendedActivityIsUngradable: extendedActivityIsUngradable,
  activityIsScorable: activityIsScorable,
  stepIsScorable: stepIsScorable,
  stepHasSubsteps: stepHasSubsteps,
  getSubstepCount: getSubstepCount,
  getSequenceProgress: getSequenceProgress,
  getActivityProgress: getActivityProgress,
  getActivitiesWithProgress: getActivitiesWithProgress,
  getCourseProgressInfo: getCourseProgressInfo
};